import {
  Mutation,
  MutationCache,
  Query,
  QueryCache,
  QueryClient,
} from "@tanstack/react-query";
import { toast } from "react-toastify";
import { ApiError } from "@/lib/apiClient";
import { clearClientAuthState } from "@/lib/auth/clearClientAuthState";
import { getUnauthorizedHandler } from "@/lib/auth/unauthorizedHandler";
import { shouldSuppressGlobalErrorToast } from "./queryMeta";

/** Pre-login routes where 401 means "not authenticated yet", not "session expired". */
const GUEST_PATHS = [
  "/login",
  "/forgotpassword",
  "/setpassword",
] as const;

function isGuestPath(pathname: string = window.location.pathname): boolean {
  return GUEST_PATHS.some((path) => pathname.endsWith(path));
}

let isHandlingUnauthorized = false;

async function handleUnauthorized() {
  if (isHandlingUnauthorized || isGuestPath()) {
    return;
  }

  isHandlingUnauthorized = true;

  try {
    const handler = getUnauthorizedHandler();
    if (handler) {
      await handler();
      return;
    }

    // UnauthorizedSessionBridge not mounted yet — clear locally and hard-navigate.
    await clearClientAuthState();
    window.location.replace("/login");
  } catch (error) {
    console.error("Error during session expiry cleanup:", error);
    window.location.replace("/login");
  } finally {
    isHandlingUnauthorized = false;
  }
}

function isOrganizationQuery(query: Query): boolean {
  return (
    query.queryKey.length >= 2 &&
    query.queryKey[0] === "auth" &&
    query.queryKey[1] === "organization"
  );
}

/** Auth probes — 401 means "not logged in", not "session expired". */
function isAuthStatusQuery(query: Query): boolean {
  const [root, segment] = query.queryKey;
  return (
    (root === "users" && segment === "me") ||
    (root === "auth" && segment === "organization")
  );
}

function showErrorToast(error: unknown, toastId?: string) {
  const message =
    error instanceof ApiError ? error.message : "An unexpected error occurred";
  toast.error(message, toastId ? { toastId } : undefined);
}

function handleQueryError(error: unknown, query: Query) {
  if (error instanceof ApiError && error.statusCode === 401) {
    if (!isAuthStatusQuery(query)) {
      void handleUnauthorized();
    }
    return;
  }
  if (shouldSuppressGlobalErrorToast(query.meta)) return;

  // Org fetch while already logged in: toast only (no logout). Dedupe repeated refetch errors.
  if (isOrganizationQuery(query)) {
    showErrorToast(error, "organizationFetchError");
    return;
  }

  showErrorToast(error);
}

function handleMutationError(
  error: unknown,
  _variables: unknown,
  _context: unknown,
  mutation: Mutation<unknown, unknown, unknown, unknown>,
) {
  if (error instanceof ApiError && error.statusCode === 401) {
    if (!isGuestPath()) {
      void handleUnauthorized();
    }
    return;
  }
  if (shouldSuppressGlobalErrorToast(mutation.meta)) return;
  showErrorToast(error);
}

export const queryClient = new QueryClient({
  queryCache: new QueryCache({ onError: handleQueryError }),
  mutationCache: new MutationCache({ onError: handleMutationError }),
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5,
      gcTime: 1000 * 60 * 10,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});
